import type { CompileContext } from "../compile/context.ts";
import { terrainNamer, type TerrainNameTable } from "./terrain-names.ts";
import { terrainNeeds } from "./terrain-needs.ts";

/**
 * The terrains of the pack index (ADR-129 amendment, A4): what the terrain palette offers beyond the packs a
 * world lists. One entry for each corner terrain of each pack, and one sample for each of its colours, on the
 * pack's sheet after the pack's prop cells. A host builds it from the compile contexts of its pack table.
 */

/**
 * One wang colour of an indexed terrain: the number a layer's corners hold, the display name the palette gives
 * the entry, and the place of its sample on the pack's sheet, counted from 0 within the pack.
 */
export interface PackIndexTerrainColour {
  readonly colour: number;
  readonly name: string;
  readonly cell: number;
}

/**
 * One corner terrain of a pack. `id` is the terrain's id as a terrain layer stores it, `tileset` its own
 * tileset, `colours` its wang colours in the pack's order, and `needs` the tilesets a first stroke adds, as
 * `terrainNeeds` lists them.
 */
export interface PackIndexTerrain {
  readonly id: string;
  readonly pack: string;
  readonly tileset: string;
  readonly name: string;
  readonly colours: readonly PackIndexTerrainColour[];
  readonly needs: readonly string[];
}

/**
 * A pack whose terrains the index could not hold, and why, in the host's words.
 */
export interface PackIndexFault {
  readonly pack: string;
  readonly message: string;
}

/**
 * One pack's input to the terrain list: its id and display name, its compile context, the first cell free on
 * its sheet (the count of its prop cells), and its rename table when it has one.
 */
export interface PackTerrainSource {
  readonly pack: string;
  readonly name: string;
  readonly context: Pick<CompileContext, "terrains" | "ruleSets">;
  readonly firstCell: number;
  readonly terrainNames?: TerrainNameTable;
}

/**
 * The terrain entries of the packs given, in their order: each pack's corner terrains in its context's order,
 * each colour taking the next cell of the pack's sheet from `firstCell`. An edge or mixed set yields nothing,
 * as the palette lists corner terrains alone.
 *
 * @param packs The packs, in the host's pack table order.
 * @returns The entries, plain data that shares nothing with the inputs.
 * @throws Error when a terrain names a rule set its context lacks.
 */
export function packIndexTerrains(packs: readonly PackTerrainSource[]): PackIndexTerrain[] {
  const entries: PackIndexTerrain[] = [];
  for (const source of packs) {
    const namer = terrainNamer(source.context.terrains, [{ id: source.pack, name: source.name, ...(source.terrainNames === undefined ? {} : { terrainNames: source.terrainNames }) }], undefined);
    let cell = source.firstCell;
    for (const terrain of source.context.terrains) {
      if (terrain.type !== "corner") {
        continue;
      }
      const colours = terrain.colors.map((colour): PackIndexTerrainColour => {
        const place = cell;
        cell += 1;
        return { colour: colour.index, name: namer.name(terrain.id, colour.index), cell: place };
      });
      entries.push({ id: terrain.id, pack: source.pack, tileset: terrain.tileset, name: terrain.name, colours, needs: terrainNeeds(source.context, terrain.id) });
    }
  }
  return entries;
}
